import { API_URL } from './config';

const TOKEN_KEY = 'crowdcam_token';

export interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
}

export interface Event {
  id: string;
  name: string;
  creatorId: string;
  createdAt: string;
  isActive: boolean;
  streamUrl: string;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  console.log('API request to:', path, 'Token:', token ? `${token.substring(0, 20)}...` : 'null');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('Authorization header added');
  } else {
    console.log('No token found in localStorage');
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(response.status, error.message || 'Request failed');
  }

  return response.json();
}

export const api = {
  async getCurrentUser(): Promise<User | null> {
    try {
      return await request<User>('/auth/me');
    } catch {
      return null;
    }
  },

  async logout(): Promise<void> {
    await request('/auth/logout', { method: 'POST' });
    clearToken();
  },

  async createEvent(name: string): Promise<Event> {
    return request<Event>('/api/events', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async getEvents(): Promise<Event[]> {
    return request<Event[]>('/api/events');
  },

  async getEvent(id: string): Promise<Event> {
    return request<Event>(`/api/events/${id}`);
  },

  async deleteEvent(id: string): Promise<void> {
    await request(`/api/events/${id}`, { method: 'DELETE' });
  },
};
